import { withCredentials } from "../../apiClient";
import { buildApiUrl } from "../../config/api";
import { parseErrorResponse } from "../../utils/apiError";
import { checkAuthStatus } from "../../utils/authStatus";

export const ENHANCE_PROMPT_PHASES = [
  "Evaluating prompt strength",
  "Rewriting",
  "Polishing",
  "Almost Done",
] as const;

export const ENHANCE_PROMPT_PHASE_MS = 1800;
export const ENHANCE_PROMPT_REQUEST_TIMEOUT_MS = 35_000;

interface LlmChatResponse {
  choices?: Array<{
    message?: {
      content?: string | null;
    };
  }>;
  error?: string | { message?: string };
  message?: string;
}

const PROMPT_JSON_KEYS = [
  "enhancedPrompt",
  "improvedPrompt",
  "enhanced_prompt",
  "improved_prompt",
] as const;

function stripJsonFence(content: string): string {
  const trimmed = content.trim();
  const fenceMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  return fenceMatch ? fenceMatch[1].trim() : trimmed;
}

function parseJsonObject(content: string): Record<string, unknown> | null {
  const stripped = stripJsonFence(content);

  const parseCandidate = (candidate: string): Record<string, unknown> | null => {
    try {
      const parsed: unknown = JSON.parse(candidate);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  };

  const parsed = parseCandidate(stripped);
  if (parsed) return parsed;

  const objectMatch = stripped.match(/\{[\s\S]*\}/);
  return objectMatch ? parseCandidate(objectMatch[0]) : null;
}

export function parseEnhancedPrompt(
  content: string
): { enhancedPrompt: string } | null {
  const parsed = parseJsonObject(content);

  if (parsed) {
    for (const key of PROMPT_JSON_KEYS) {
      const candidate = parsed[key];
      if (typeof candidate === "string" && candidate.trim()) {
        return { enhancedPrompt: candidate.trim() };
      }
    }
  }

  const stripped = stripJsonFence(content).trim();
  if (stripped && !stripped.startsWith("{")) {
    return { enhancedPrompt: stripped };
  }

  return null;
}

function readResponseError(data: LlmChatResponse | null): string | undefined {
  if (typeof data?.message === "string" && data.message) {
    return data.message;
  }
  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }
  if (
    data?.error &&
    typeof data.error === "object" &&
    typeof data.error.message === "string" &&
    data.error.message
  ) {
    return data.error.message;
  }
  return undefined;
}

export async function enhancePrompt(prompt: string): Promise<string> {
  const normalizedPrompt = prompt.trim();
  if (!normalizedPrompt) {
    throw new Error("Enter a prompt before enhancing it.");
  }

  const abortController =
    typeof AbortController === "undefined" ? null : new AbortController();
  let timeoutId: number | undefined;
  let timedOut = false;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => {
      timedOut = true;
      abortController?.abort();
      reject(new Error("Prompt enhancement timed out. Please try again."));
    }, ENHANCE_PROMPT_REQUEST_TIMEOUT_MS);
  });

  try {
    const response = await Promise.race([
      fetch(
        buildApiUrl("/api/finster/enhance-prompt"),
        withCredentials({
          method: "POST",
          body: JSON.stringify({ prompt: normalizedPrompt }),
          ...(abortController ? { signal: abortController.signal } : {}),
        })
      ),
      timeoutPromise,
    ]);

    checkAuthStatus(response);
    const errorResponse = response.clone();
    const data = (await response.json().catch(() => null)) as LlmChatResponse | null;

    if (!response.ok) {
      const fallbackMessage = await parseErrorResponse(errorResponse);
      throw new Error(readResponseError(data) || fallbackMessage || "Failed to enhance prompt.");
    }

    const responseError = readResponseError(data);
    if (responseError) {
      throw new Error(responseError);
    }

    const content = data?.choices?.[0]?.message?.content;
    if (!content) {
      throw new Error("No response content from LLM.");
    }

    const parsed = parseEnhancedPrompt(content);
    if (!parsed) {
      throw new Error("Malformed enhanced prompt response.");
    }

    return parsed.enhancedPrompt;
  } catch (error) {
    if (timedOut || (error instanceof Error && error.name === "AbortError")) {
      throw new Error("Prompt enhancement timed out. Please try again.");
    }
    throw error;
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}
